/**
 * Global search index builder for standards and lessons
 */

// Build index of all standards from JSON files
export function buildStandardsIndex() {
  let context
  try {
    context = require.context('../data/standards', false, /\.json$/)
  } catch (e) {
    return []
  }

  const items = []
  const codeMap = new Map()

  context.keys().forEach((key) => {
    if (key.includes('aliases.json')) return // Skip aliases

    const data = context(key)
    const subject = data.subject || 'Unknown'
    const gradeLabel = data.gradeLabel || 'Unknown'

    for (const std of data.standards || []) {
      if (codeMap.has(std.code)) continue // Dedupe

      items.push({
        type: 'standard',
        code: std.code,
        statement: std.statement,
        subject,
        gradeLabel,
        path: std.path || [],
        searchText: `${std.code} ${std.statement} ${subject} ${gradeLabel}`.toLowerCase(),
      })
      codeMap.set(std.code, true)
    }
  })

  return items
}

// Helper to safely extract text from various data structures
function extractText(value) {
  if (!value) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'object' && value.text) return value.text
  if (Array.isArray(value)) {
    return value.map(extractText).filter(Boolean).join(' ')
  }
  return ''
}

// Build index of all ELA lessons
export function buildElaLessonsIndex() {
  let context
  try {
    context = require.context('../data/ela', true, /\.json$/)
  } catch (e) {
    return []
  }

  const items = []

  context.keys().forEach((key) => {
    try {
      const data = context(key)
      if (!data.title) return

      // Extract metadata from filename or data
      const grade = data.grade || 'Unknown'
      const gradeLabel = `Grade ${grade}`
      const unit = data.unit || 'Unknown'
      const lesson = data.lesson || 'Unknown'
      const subject = data.subject || 'ELA'

      // Build searchable text from all relevant fields
      const searchParts = [
        data.title,
        `Grade ${grade}`,
        `Unit ${unit}`,
        `Lesson ${lesson}`,
        subject,
        extractText(data.essentialQuestion),
        extractText(data.guidingQuestion),
        extractText(data.learningObjectives),
        extractText(data.standards?.addressed),
        extractText(data.standards?.assessed),
        extractText(data.vocabulary),
      ]

      items.push({
        type: 'lesson',
        subject,
        gradeLabel,
        grade,
        unit,
        lesson,
        title: data.title,
        essentialQuestion: extractText(data.essentialQuestion),
        key, // Store path for potential navigation
        searchText: searchParts.filter(Boolean).join(' ').toLowerCase(),
      })
    } catch (e) {
      // Skip malformed files
    }
  })

  return items
}

// Build index of all Social Science lessons
export function buildSsLessonsIndex() {
  let context
  try {
    context = require.context('../data/ss', true, /\.json$/)
  } catch (e) {
    return []
  }

  const items = []

  context.keys().forEach((key) => {
    try {
      const data = context(key)
      if (!data.title) return

      // Extract metadata
      const gradeLabel = data.gradeLabel || 'K'
      const unit = data.unit || 'Unknown'
      const lesson = data.lesson || 'Unknown'
      const subject = data.subject || 'Social Science'

      // Build searchable text
      const searchParts = [
        data.title,
        gradeLabel,
        `Unit ${unit}`,
        `Lesson ${lesson}`,
        subject,
        extractText(data.essentialQuestion),
        extractText(data.guidingQuestion),
        extractText(data.learningObjectives),
        extractText(data.standards?.addressed),
        extractText(data.vocabulary),
      ]

      items.push({
        type: 'lesson',
        subject,
        gradeLabel,
        unit,
        lesson,
        title: data.title,
        essentialQuestion: extractText(data.essentialQuestion),
        key,
        searchText: searchParts.filter(Boolean).join(' ').toLowerCase(),
      })
    } catch (e) {
      // Skip malformed files
    }
  })

  return items
}

// Build complete search index
export function buildSearchIndex() {
  return [
    ...buildStandardsIndex(),
    ...buildElaLessonsIndex(),
    ...buildSsLessonsIndex(),
  ]
}

// Search the index with a query string
export function searchIndex(index, query) {
  if (!query || query.trim().length === 0) return []

  const terms = query.toLowerCase().trim().split(/\s+/)
  
  // Score each item based on how many terms match
  const scored = index
    .map(item => {
      let score = 0
      
      for (const term of terms) {
        if (item.searchText.includes(term)) {
          score++
          
          // Boost if term appears in title or code
          if (item.type === 'standard' && item.code?.toLowerCase().includes(term)) {
            score += 3
          }
          if (item.type === 'lesson' && item.title?.toLowerCase().includes(term)) {
            score += 2
          }
        }
      }
      
      return { item, score }
    })
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .map(({ item }) => item)
    .slice(0, 50) // Limit to top 50 results

  return scored
}
